import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';
import { formatDate } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  'Resume/CV',
  'ID Proof',
  'Address Proof',
  'Education Certificate',
  'Experience Letter',
  'Medical Certificate',
  'Salary Slip',
  'Offer Letter',
  'Joining Letter',
  'Resignation Letter',
  'Cancel Cheque Copy',
  'Other'
];

function Documents({ user, onLogout }) {
  const [documents, setDocuments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterDocType, setFilterDocType] = useState('');
  const [dashboardStats, setDashboardStats] = useState(null);
  
  const [formData, setFormData] = useState({
    employee_id: '',
    document_type: 'Resume/CV',
    document_name: '',
    description: '',
    expiry_date: '',
    file: null
  });

  useEffect(() => {
    fetchDocuments();
    fetchDashboardStats();
    if (user.role === 'Admin' || user.role === 'HR Manager') {
      fetchEmployees();
    }
  }, [filterEmployee, filterDocType]);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/documents?`;
      if (filterEmployee) url += `employee_id=${filterEmployee}&`;
      if (filterDocType) url += `document_type=${filterDocType}&`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      setFormData({ ...formData, file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file) {
      alert('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const uploadData = new FormData();
      uploadData.append('file', formData.file);

      let uploadUrl = `${API}/documents/upload?employee_id=${formData.employee_id}&document_type=${encodeURIComponent(formData.document_type)}&document_name=${encodeURIComponent(formData.document_name)}`;
      if (formData.description) {
        uploadUrl += `&description=${encodeURIComponent(formData.description)}`;
      }
      if (formData.expiry_date) {
        uploadUrl += `&expiry_date=${formData.expiry_date}`;
      }

      await axios.post(uploadUrl, uploadData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      alert('Document uploaded successfully!');
      setShowUploadForm(false);
      resetForm();
      fetchDocuments();
    } catch (error) {
      alert('Failed to upload document: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (documentId, fileName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/documents/${documentId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Failed to download document: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleVerify = async (documentId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/documents/${documentId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Document verified successfully!');
      fetchDocuments();
    } catch (error) {
      alert('Failed to verify document: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Document deleted successfully!');
      fetchDocuments();
    } catch (error) {
      alert('Failed to delete document: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      document_type: 'Resume/CV',
      document_name: '',
      description: '',
      expiry_date: '',
      file: null
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Document Management</h1>
          <button
            onClick={() => { setShowUploadForm(true); resetForm(); }}
            className="px-6 py-2 rounded-lg text-white hover:opacity-90"
            style={{backgroundColor: '#2b3380'}}
          >
            Upload Document
          </button>
        </div>

        {/* Document Management Mini Dashboard */}
        {dashboardStats && (
          <MiniDashboard
            title="Document Management Overview"
            icon="📄"
            bgColor="#ff5722"
            stats={[
              {
                label: 'Pending Documents',
                value: dashboardStats.pending_documents || 0,
                subtitle: 'Awaiting verification',
                color: '#ff9800'
              },
              {
                label: 'Total Documents',
                value: dashboardStats.total_documents || 0,
                subtitle: 'All documents',
                color: '#ff5722'
              },
              {
                label: 'Total Employees',
                value: dashboardStats.total_employees || 0,
                subtitle: 'Document holders',
                color: '#2b3380'
              },
              {
                label: 'Document Types',
                value: '12',
                subtitle: 'Supported types',
                color: '#607d8b'
              }
            ]}
          />
        )}

        {/* Filters */}
        {(user.role === 'Admin' || user.role === 'HR Manager') && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Filter by Employee</label>
                <select
                  value={filterEmployee}
                  onChange={(e) => setFilterEmployee(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">All Employees</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} ({emp.id})
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Filter by Document Type</label>
                <select
                  value={filterDocType}
                  onChange={(e) => setFilterDocType(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">All Types</option>
                  {DOCUMENT_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Upload Form Modal */}
        {showUploadForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>Upload Document</h2>
                  <button
                    onClick={() => setShowUploadForm(false)}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {(user.role === 'Admin' || user.role === 'HR Manager') && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Employee *</label>
                      <select
                        value={formData.employee_id}
                        onChange={(e) => setFormData({...formData, employee_id: e.target.value})}
                        required
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="">Select Employee</option>
                        {employees.map(emp => (
                          <option key={emp.id} value={emp.id}>
                            {emp.first_name} {emp.last_name} ({emp.id})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Document Type *</label>
                      <select
                        value={formData.document_type}
                        onChange={(e) => setFormData({...formData, document_type: e.target.value})}
                        required
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        {DOCUMENT_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Expiry Date (Optional)</label>
                      <input
                        type="date"
                        value={formData.expiry_date}
                        onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Document Name *</label>
                    <input
                      type="text"
                      value={formData.document_name}
                      onChange={(e) => setFormData({...formData, document_name: e.target.value})}
                      required
                      placeholder="e.g., Aadhar Card, Degree Certificate"
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Description (Optional)</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      rows="2"
                      placeholder="Additional information about the document"
                      className="w-full px-3 py-2 border rounded-lg"
                    ></textarea>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Select File * (Max 10MB)</label>
                    <input
                      type="file"
                      onChange={handleFileChange}
                      required
                      className="w-full px-3 py-2 border rounded-lg"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    />
                    {formData.file && (
                      <p className="text-sm text-gray-600 mt-1">
                        Selected: {formData.file.name} ({formatFileSize(formData.file.size)})
                      </p>
                    )}
                  </div>

                  <div className="flex gap-3 justify-end">
                    <button
                      type="button"
                      onClick={() => setShowUploadForm(false)}
                      className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                      disabled={uploading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 rounded-lg text-white disabled:opacity-50"
                      style={{backgroundColor: '#2b3380'}}
                      disabled={uploading}
                    >
                      {uploading ? 'Uploading...' : 'Upload Document'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Documents List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {documents.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <p className="text-lg">No documents found.</p>
              <p className="mt-2">Upload your first document to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead style={{backgroundColor: '#2b3380', color: 'white'}}>
                  <tr>
                    <th className="px-4 py-3 text-left">Document</th>
                    <th className="px-4 py-3 text-left">Employee</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-left">File Name</th>
                    <th className="px-4 py-3 text-left">Size</th>
                    <th className="px-4 py-3 text-left">Uploaded</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium">{doc.document_name}</div>
                        {doc.description && (
                          <div className="text-sm text-gray-500">{doc.description}</div>
                        )}
                        {doc.expiry_date && (
                          <div className="text-xs text-red-600">Expires: {formatDate(doc.expiry_date)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{doc.employee_name}</div>
                        <div className="text-sm text-gray-500">{doc.employee_id}</div>
                      </td>
                      <td className="px-4 py-3 text-sm">{doc.document_type}</td>
                      <td className="px-4 py-3 text-sm">{doc.file_name}</td>
                      <td className="px-4 py-3 text-sm">{formatFileSize(doc.file_size)}</td>
                      <td className="px-4 py-3 text-sm">
                        <div>{formatDate(doc.created_at)}</div>
                        {doc.uploader_name && (
                          <div className="text-xs text-gray-500">by {doc.uploader_name}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {doc.is_verified ? (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            Verified
                          </span>
                        ) : (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => handleDownload(doc.id, doc.file_name)}
                            className="text-[#2b3380] hover:text-[#2b3380] text-sm font-medium"
                          >
                            Download
                          </button>
                          {(user.role === 'Admin' || user.role === 'HR Manager') && !doc.is_verified && (
                            <button
                              onClick={() => handleVerify(doc.id)}
                              className="text-green-600 hover:text-green-800 text-sm font-medium"
                            >
                              Verify
                            </button>
                          )}
                          {(user.role === 'Admin' || user.role === 'HR Manager') && (
                            <button
                              onClick={() => handleDelete(doc.id)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Documents;
